import { filterIntersections, intersection, intersects } from './array';
/**
 * Tests to see if any keys in an object intersect with any keys in a provided list
 *
 * @param object Object to test keys of
 * @param keys List of keys to test against
 */
// eslint-disable-next-line @typescript-eslint/ban-types
export const includesSomeKeys = (object, keys) => intersects(Object.keys(object), keys);
/**
 * Determines if an object is empty or not
 *
 * @param object Object to test
 */
// eslint-disable-next-line @typescript-eslint/ban-types
export const isEmpty = (object) => (object
    && Object.keys(object).length === 0
    && Object.getPrototypeOf(object) === Object.prototype);
/**
 * Reduces objects into one object (if applicable) - Doesn't spread nullable or empty objects
 *
 * @param objects Objects to be reduced
 */
// eslint-disable-next-line @typescript-eslint/ban-types
export const merge = (...objects) => {
    let spreadObject = {};
    objects.forEach((object) => {
        if (object && !isEmpty(object)) {
            spreadObject = { ...spreadObject, ...object };
        }
    });
    if (spreadObject && !isEmpty(spreadObject)) {
        return spreadObject;
    }
    return null;
};
/**
 * Compares two objects and returns the differences between them
 *
 * @param originalObject The object containing the original values to be tested
 * @param newObject The object containing the new values to be tested
 */
export const shallowDiff = (originalObject, newObject) => {
    const diff = {
        added: {},
        removed: {},
        changed: {},
        unchanged: {},
    };
    const originalKeys = Object.keys(originalObject);
    const newKeys = Object.keys(newObject);
    const keyDiff = filterIntersections(originalKeys, newKeys);
    const keyIntersection = intersection(originalKeys, newKeys);
    keyDiff.forEach((key) => {
        if (key in originalObject) {
            diff.removed[key] = originalObject[key];
        }
        else {
            diff.added[key] = newObject[key];
        }
    });
    keyIntersection.forEach((key) => {
        if (originalObject[key] === newObject[key]) {
            diff.unchanged[key] = originalObject[key];
        }
        else {
            diff.changed[key] = {
                newValue: newObject[key],
                originalValue: originalObject[key],
            };
        }
    });
    return diff;
};
/**
 * Returns the parent class for the provided class. If no parent exists (the class doesn't
 * explicitly extend another class) then `null` is returned.
 *
 * NOTE: All classes implicitly extend the Function object, which is ignored as a parent
 * in this function
 *
 * @param child A class or prototype to fetch an inherited parent for
 */
// eslint-disable-next-line @typescript-eslint/ban-types
export const getSuper = (child) => {
    // If we're working with an instance or a prototype, then we need to use the constructor
    // to fetch the parent
    if ('constructor' in child && child.constructor !== Function) {
        return getSuper(Object.getPrototypeOf(child).constructor);
    }
    const parent = Object.getPrototypeOf(child);
    // If the parent is Function, then there's no explicit parent class
    if (parent === Function.prototype) {
        return null;
    }
    return parent;
};
